import { n as themeColor_v0_v2, r as defaultThemeConfig } from "./getTheme_v2-y09KqtEb.js";
/**
* Defines a lazy, self-replacing property on `obj`.
*
* On first access the `factory` is called and the getter is replaced with the
* computed plain value — subsequent reads have zero overhead.
*
* The property is enumerable (visible in `Object.keys` / spread) in both states.
*
* @internal
*/
function defineLazyProperty(obj, key, factory) {
	Object.defineProperty(obj, key, {
		get() {
			let value = factory();
			return Object.defineProperty(obj, key, {
				value,
				enumerable: !0,
				writable: !1,
				configurable: !1
			}), value;
		},
		enumerable: !0,
		configurable: !0
	});
}
/** @internal */
function is_v2(themeProp) {
	return themeProp._version === 2;
}
/**
* Apply `neutral` and `suggest` if they're not already part of the color object,
* as this was introduced in v2.9, but is not compatible with v2.0.
*
* @param color - The color object to upgrade
* @returns The upgraded color object. Returns as-is if already upgraded.
* @internal
*/
function themeColor_v0_v2_9(color) {
	if ("neutral" in color.badge) return color;
	let colors = color;
	return {
		...colors,
		badge: {
			...colors.badge,
			neutral: colors.badge.default,
			suggest: colors.badge.primary
		},
		button: {
			bleed: {
				...colors.button.bleed,
				neutral: colors.button.bleed.default,
				suggest: colors.button.bleed.primary
			},
			default: {
				...colors.button.default,
				neutral: colors.button.default.default,
				suggest: colors.button.default.primary
			},
			ghost: {
				...colors.button.ghost,
				neutral: colors.button.ghost.default,
				suggest: colors.button.ghost.primary
			}
		},
		selectable: {
			...colors.selectable,
			neutral: colors.selectable.default,
			suggest: colors.selectable.primary
		}
	};
}
const cache$2 = /* @__PURE__ */ new WeakMap();
/** @internal */
function v0_v2(v0) {
	if (v0.v2) return v0.v2;
	let cached_v2 = cache$2.get(v0);
	if (cached_v2) return cached_v2;
	let { avatar, button, color, container, focusRing: _unused_focusRing, fonts: font, input, layer, media, radius, shadows: shadow, space, styles: style } = v0, v2 = {
		_version: 2,
		avatar: {
			...defaultThemeConfig.avatar,
			...avatar
		},
		button: {
			...defaultThemeConfig.button,
			...button
		},
		card: defaultThemeConfig.card,
		color: {
			light: {
				transparent: themeColor_v0_v2(color.light.transparent),
				default: themeColor_v0_v2(color.light.default),
				neutral: themeColor_v0_v2(color.light.transparent),
				primary: themeColor_v0_v2(color.light.primary),
				suggest: themeColor_v0_v2(color.light.primary),
				positive: themeColor_v0_v2(color.light.positive),
				caution: themeColor_v0_v2(color.light.caution),
				critical: themeColor_v0_v2(color.light.critical)
			},
			dark: {
				transparent: themeColor_v0_v2(color.dark.transparent),
				default: themeColor_v0_v2(color.dark.default),
				neutral: themeColor_v0_v2(color.dark.transparent),
				primary: themeColor_v0_v2(color.dark.primary),
				suggest: themeColor_v0_v2(color.dark.primary),
				positive: themeColor_v0_v2(color.dark.positive),
				caution: themeColor_v0_v2(color.dark.caution),
				critical: themeColor_v0_v2(color.dark.critical)
			}
		},
		container,
		font,
		input: {
			...defaultThemeConfig.input,
			...input,
			checkbox: {
				...defaultThemeConfig.input.checkbox,
				...input.checkbox
			},
			radio: {
				...defaultThemeConfig.input.radio,
				...input.radio
			},
			switch: {
				...defaultThemeConfig.input.switch,
				...input.switch
			}
		},
		layer: layer ?? defaultThemeConfig.layer,
		media,
		radius,
		shadow,
		space,
		style
	};
	return cache$2.set(v0, v2), v2;
}
const cache$1 = /* @__PURE__ */ new WeakMap(), V0_TONES = [
	"transparent",
	"default",
	"primary",
	"positive",
	"caution",
	"critical"
];
function lazyV0Scheme(schemeKey, color) {
	let scheme = {};
	for (let tone of V0_TONES) defineLazyProperty(scheme, tone, () => themeColor_v2_v0(color[schemeKey][tone]));
	return scheme;
}
/** @internal */
function v2_v0(v2) {
	let cachedTheme = cache$1.get(v2);
	if (cachedTheme) return cachedTheme;
	let { avatar, button, color, container, font: fonts, input, media, radius, shadow: shadows, space, style: styles } = v2, v0Color = {};
	defineLazyProperty(v0Color, "light", () => lazyV0Scheme("light", color)), defineLazyProperty(v0Color, "dark", () => lazyV0Scheme("dark", color));
	let theme = {
		_version: 0,
		avatar,
		button,
		container,
		color: v0Color,
		focusRing: input.text.focusRing,
		fonts,
		input,
		media,
		radius,
		shadows,
		space,
		styles,
		v2
	};
	return cache$1.set(v2, theme), theme;
}
function themeColor_v2_v0(color_v2) {
	return {
		base: {
			bg: color_v2.bg,
			fg: color_v2.fg,
			border: color_v2.border,
			focusRing: color_v2.focusRing,
			shadow: color_v2.shadow
		},
		button: color_v2.button,
		card: color_v2.selectable.default,
		dark: color_v2._dark,
		input: {
			default: inputStatesThemeColor_v2_v0(color_v2.input.default),
			invalid: inputStatesThemeColor_v2_v0(color_v2.input.invalid)
		},
		muted: {
			...color_v2.button.ghost,
			transparent: color_v2.button.ghost.default
		},
		solid: {
			...color_v2.button.default,
			transparent: color_v2.button.default.default
		},
		selectable: color_v2.selectable,
		spot: {
			gray: color_v2.avatar.gray.bg,
			blue: color_v2.avatar.blue.bg,
			purple: color_v2.avatar.purple.bg,
			magenta: color_v2.avatar.magenta.bg,
			red: color_v2.avatar.red.bg,
			orange: color_v2.avatar.orange.bg,
			yellow: color_v2.avatar.yellow.bg,
			green: color_v2.avatar.green.bg,
			cyan: color_v2.avatar.cyan.bg
		},
		syntax: color_v2.syntax
	};
}
function inputStatesThemeColor_v2_v0(t) {
	return {
		enabled: inputStateThemeColor_v2_v0(t.enabled),
		disabled: inputStateThemeColor_v2_v0(t.disabled),
		readOnly: inputStateThemeColor_v2_v0(t.readOnly),
		hovered: inputStateThemeColor_v2_v0(t.hovered)
	};
}
function inputStateThemeColor_v2_v0(t) {
	return {
		bg: t.bg,
		bg2: t.muted.bg,
		border: t.border,
		fg: t.fg,
		placeholder: t.placeholder
	};
}
const cache = /* @__PURE__ */ new Map();
/** @internal */
function getScopedTheme(themeProp, scheme, tone) {
	let cachedTheme = _getCachedTheme(themeProp, scheme, tone);
	if (cachedTheme) return cachedTheme;
	let v0 = is_v2(themeProp) ? v2_v0(themeProp) : themeProp, v2 = is_v2(themeProp) ? themeProp : v0_v2(themeProp), layer_v0 = v0.layer || defaultThemeConfig.layer, colorScheme_v2 = v2.color[scheme] || v2.color.light, color_v2_9 = themeColor_v0_v2_9(colorScheme_v2[tone] || colorScheme_v2.default), layer_v2 = v2.layer || defaultThemeConfig.layer, { color: _v0Color, ...v0Rest } = v0, sanity = {
		...v0Rest,
		layer: layer_v0,
		v2: {
			...v2,
			_resolved: !0,
			color: color_v2_9,
			layer: layer_v2
		}
	};
	defineLazyProperty(sanity, "color", () => {
		let colorScheme_v0 = v0.color[scheme] || v0.color.light;
		return colorScheme_v0[tone] || colorScheme_v0.default;
	});
	let theme = { sanity };
	return _setCachedTheme(themeProp, scheme, tone, theme), theme;
}
function _getCachedTheme(rootTheme, scheme, tone) {
	let schemeCache = cache.get(scheme);
	if (!schemeCache) return;
	let toneCache = schemeCache.get(tone);
	if (toneCache) return toneCache.get(rootTheme);
}
function _setCachedTheme(rootTheme, scheme, tone, theme) {
	cache.has(scheme) || cache.set(scheme, /* @__PURE__ */ new Map());
	let schemeCache = cache.get(scheme);
	schemeCache.has(tone) || schemeCache.set(tone, /* @__PURE__ */ new WeakMap()), schemeCache.get(tone).set(rootTheme, theme);
}
export { defineLazyProperty as a, is_v2 as i, v2_v0 as n, v0_v2 as r, getScopedTheme as t };

//# sourceMappingURL=getScopedTheme-C-sZsyAz.js.map